/**
 * NetworkToolConfigPanel — the ONE connector/MCP config surface for a registry
 * network, used at two scopes (NETWORK-REGISTRY.md §14):
 *   • ``scope="network"`` — network-scope entries from the package's ``toolsUsed``,
 *     bound against the LIVE network (no room needed): a connector → network default
 *     (``setNetworkDefaultConnector``) or the agent's config; an MCP → a NETWORK-scope
 *     credential.
 *   • ``scope="room"``   — room-context entries, bound against a specific created
 *     room/pool: pool/agent connector overrides + POOL/AGENT MCP credentials.
 *
 * Same UX as the workbench "Tools" strip: a top-down list of the tools this network
 * needs; each is pick-existing (``InstanceListSubview``) or create-new (the reused
 * registry wizards), MCPs via ``McpCredentialForm``. Unlike the old one-shot deploy
 * wizard, this binds EACH tool immediately against the live network — so the user can
 * do 2 of 5, close, and resume later (partial save; the tile goes yellow until green).
 *
 * Reuses the exact primitives the deploy wizard proved. Owns its own persistence.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getRegistryPackageDetail,
} from '@stepflow/ui-shared/services/networkRegistryApi.js';
import {
  setNetworkDefaultConnector,
} from '@stepflow/ui-shared/services/agenticNetworksApi.js';
import {
  setPoolConnectorOverride,
  setAgentPoolConnectorOverride,
} from '@stepflow/ui-shared/services/poolMembersApi.js';
import {
  listSecretsRegistry,
  listMcpRegistry,
  createMcpCredential,
} from '@stepflow/ui-shared/services/toolsApi.js';
import McpCredentialForm from '@stepflow/ui-shared/components/mcp/McpCredentialForm.jsx';
import { InstanceListSubview } from '@stepflow/ui-shared/components/agentTools/AgentToolPicker.jsx';
import ToolCodeConnectorRegistryWizard from '@stepflow/ui-shared/components/agentTools/ToolCodeConnectorRegistryWizard.jsx';
import ToolGenericRestConfigWizard from '@stepflow/ui-shared/components/agentTools/ToolGenericRestConfigWizard.jsx';
import { scopeBadgeClass, scopeBadgeTitle } from '@stepflow/ui-shared/utils/scopeBadge.js';

const REGISTRY_PROBE_NETWORK_ID = '__registry_probe__';

function toolScopeLabel(scope, agentId) {
  if (scope === 'agent') return `agent ${agentId || ''}`.trim();
  if (scope === 'pool') return 'room context';
  return 'network';
}

// A workspace is auto-provisioned at room creation — never a user-configured entry.
function isConfigurable(t) {
  return t && (t.kind === 'connector' || t.kind === 'mcp') && t.name !== 'workspace';
}

// Which toolsUsed entries belong on THIS panel's scope. Network panel = network-scope
// entries; room panel = room-context (pool/agent) entries.
function entriesForScope(toolsUsed, scope) {
  return (toolsUsed || []).filter((t) => {
    if (!isConfigurable(t)) return false;
    const s = t.scope || 'network';
    return scope === 'network' ? s === 'network' : (s === 'pool' || s === 'agent');
  });
}

function ScopePill({ scope, agentId, roomContext }) {
  const s = scope === 'agent' ? 'agent' : scope === 'pool' ? 'pool' : 'network';
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`rounded px-1.5 py-px text-[9px] font-medium uppercase tracking-wide ring-1 ${scopeBadgeClass(s)}`}
        title={scopeBadgeTitle(s)}
      >
        {toolScopeLabel(scope, agentId)}
      </span>
      {roomContext ? <span className="text-[10px] text-amber-600">· required by the proven room</span> : null}
    </span>
  );
}

// One connector entry — pick an existing TESTED_OK connection or create one inline.
function ConnectorCard({ entry, connectors, picked, saving, onPick, onCreate }) {
  const items = (connectors || [])
    .filter((s) => (s.toolName || 'code-connector') === entry.name)
    .map((s) => {
      const ok = String(s.testStatus || '').toUpperCase() === 'TESTED_OK';
      return {
        key: s.secretId,
        testid: `toolcfg-connector-tile-${entry.key}-${s.secretId}`,
        label: s.secretName || s.secretId,
        tag: s.secretId === picked ? 'selected' : (ok ? null : 'not tested'),
        disabled: !ok,
        disabledReason: 'This connection has not passed its test yet — open it in Connections to test.',
      };
    });
  return (
    <div className="rounded-md border border-gray-200 p-2" data-testid={`toolcfg-connector-${entry.key}`}>
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-gray-700">
        {entry.name}
        <ScopePill scope={entry.scope} agentId={entry.agentId} roomContext={entry.roomContext} />
        {picked ? <span className="text-[10px] font-medium text-emerald-600">✓ configured</span> : null}
        {saving ? <span className="text-[10px] text-gray-400">saving…</span> : null}
      </div>
      <InstanceListSubview
        showBack={false}
        testidPrefix={`toolcfg-connector-${entry.key}`}
        items={items}
        onPick={onPick}
        onCreate={onCreate}
        createLabel="+ Create new connection"
        emptyHint={<>No {entry.name} connections yet. Create one below.</>}
      />
    </div>
  );
}

/**
 * @param {object}   props
 * @param {object}   props.pkg          package ref (registryNetworkId + version) OR
 * @param {object[]} [props.toolsUsed]  pre-fetched toolsUsed (skips the detail fetch)
 * @param {string}   props.networkId    the LIVE deployed network to bind against
 * @param {string}   [props.poolId]     required when scope="room"
 * @param {'network'|'room'} props.scope
 * @param {string}   props.tenantId
 * @param {(summary:{configured:number,total:number})=>void} [props.onProgress]
 * @param {()=>void} [props.onDone]     "Save & continue" / "Finish" clicked
 */
export default function NetworkToolConfigPanel({
  pkg, toolsUsed: toolsUsedProp, networkId, poolId = '', scope = 'network',
  tenantId = '', onProgress, onDone, doneLabel = 'Save & continue',
}) {
  const registryNetworkId = String(pkg?.registryNetworkId || '');
  const version = Number(pkg?.version) || undefined;

  const [toolsUsed, setToolsUsed] = useState(Array.isArray(toolsUsedProp) ? toolsUsedProp : []);
  const [mcpRegistry, setMcpRegistry] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(!Array.isArray(toolsUsedProp));
  const [error, setError] = useState(null);

  // Per-entry configured state (secretId for connectors; true for saved MCP creds).
  const [done, setDone] = useState({});       // { [key]: secretId | true }
  const [savingKey, setSavingKey] = useState('');
  const [createFor, setCreateFor] = useState(null);

  const scopedEntries = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of entriesForScope(toolsUsed, scope)) {
      const s = t.scope || 'network';
      const agentId = t.agentId || '';
      const key = `${t.kind}|${t.name}|${s}|${agentId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, kind: t.kind, name: t.name, scope: s, agentId, roomContext: !!t.roomContext });
    }
    return out;
  }, [toolsUsed, scope]);

  const connectorEntries = useMemo(() => scopedEntries.filter((e) => e.kind === 'connector'), [scopedEntries]);
  const mcpEntries = useMemo(() => scopedEntries.filter((e) => e.kind === 'mcp'), [scopedEntries]);

  const refreshConnectors = useCallback(async () => {
    const res = await listSecretsRegistry({}).catch(() => ({ secrets: [] }));
    const rows = Array.isArray(res?.secrets) ? res.secrets : [];
    setConnectors(rows.filter((r) => r.toolName && r.toolName !== 'skill' && r.toolName !== 'workspace'));
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setError(null);
      try {
        const [detail, mcpReg] = await Promise.all([
          Array.isArray(toolsUsedProp)
            ? Promise.resolve({ toolsUsed: toolsUsedProp })
            : getRegistryPackageDetail(registryNetworkId, version).catch(() => ({})),
          listMcpRegistry().catch(() => ({ mcps: [] })),
        ]);
        if (!alive) return;
        if (!Array.isArray(toolsUsedProp)) {
          setToolsUsed(Array.isArray(detail?.toolsUsed) ? detail.toolsUsed : []);
        }
        setMcpRegistry(Array.isArray(mcpReg?.mcps) ? mcpReg.mcps : (Array.isArray(mcpReg) ? mcpReg : []));
        await refreshConnectors();
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [registryNetworkId, version, toolsUsedProp, refreshConnectors]);

  const configuredCount = useMemo(
    () => scopedEntries.filter((e) => done[e.key]).length, [scopedEntries, done]);

  useEffect(() => {
    onProgress?.({ configured: configuredCount, total: scopedEntries.length });
  }, [configuredCount, scopedEntries.length, onProgress]);

  // Bind ONE connector against the live network/room at its scope, then mark done.
  const bindConnector = useCallback(async (entry, secretId) => {
    const sid = String(secretId || '').trim();
    if (!sid) return;
    setSavingKey(entry.key);
    setError(null);
    try {
      if (entry.scope === 'network') {
        await setNetworkDefaultConnector(networkId, sid);
      } else if (entry.scope === 'agent' && entry.agentId) {
        if (!poolId) throw new Error('This connector binds per-agent in a room — create a room first.');
        await setAgentPoolConnectorOverride(networkId, poolId, entry.agentId, entry.name, { secretId: sid });
      } else {
        if (!poolId) throw new Error('This connector binds at room context — create a room first.');
        await setPoolConnectorOverride(networkId, poolId, entry.name, { secretId: sid });
      }
      setDone((m) => ({ ...m, [entry.key]: sid }));
    } catch (e) {
      setError(e?.message || 'Failed to bind connector');
    } finally {
      setSavingKey('');
    }
  }, [networkId, poolId]);

  // Write ONE MCP credential at the entry's scope, then mark done.
  const saveMcp = useCallback(async (entry, credentials) => {
    if (!credentials || !Object.keys(credentials).length) return;
    setSavingKey(entry.key);
    setError(null);
    try {
      const s = { kind: entry.scope === 'network' ? 'NETWORK' : 'POOL', networkId };
      if (entry.scope !== 'network') {
        if (!poolId) throw new Error('This MCP binds at room scope — create a room first.');
        s.poolId = poolId;
        if (entry.scope === 'agent' && entry.agentId) s.agentId = entry.agentId;
      }
      await createMcpCredential({ mcpId: entry.name, scope: s, credentials });
      setDone((m) => ({ ...m, [entry.key]: true }));
    } catch (e) {
      setError(e?.message || 'Failed to save MCP credential');
    } finally {
      setSavingKey('');
    }
  }, [networkId, poolId]);

  const createForEntry = useMemo(
    () => connectorEntries.find((c) => c.key === createFor) || null, [connectorEntries, createFor]);

  const handleConnectorCreated = useCallback(async (saved) => {
    const sid = String(saved?.secretId || '').trim();
    const entry = createForEntry;
    setCreateFor(null);
    await refreshConnectors();
    if (sid && entry) await bindConnector(entry, sid);
  }, [createForEntry, refreshConnectors, bindConnector]);

  if (loading) {
    return <div className="flex items-center justify-center py-10 text-xs text-gray-500">Checking what this network needs…</div>;
  }

  const total = scopedEntries.length;
  const allDone = total === 0 || configuredCount === total;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-1 py-2" data-testid="toolcfg-panel">
        {total === 0 ? (
          <p className="text-sm text-gray-600" data-testid="toolcfg-empty">
            {scope === 'network'
              ? 'This network needs no network-level connectors or MCP servers.'
              : 'This room needs no room-specific connectors or MCP servers.'}
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {scope === 'network'
                  ? 'Connections this network needs. Configure each — pick an existing one or create a new one.'
                  : 'Room-specific connections. These bind to this room.'}
              </p>
              <span className="text-[11px] font-medium text-gray-500" data-testid="toolcfg-progress">
                {configuredCount}/{total} configured
              </span>
            </div>

            {connectorEntries.length ? (
              <div className="space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Code connections</div>
                {connectorEntries.map((c) => (
                  <ConnectorCard
                    key={c.key} entry={c} connectors={connectors}
                    picked={typeof done[c.key] === 'string' ? done[c.key] : ''}
                    saving={savingKey === c.key}
                    onPick={(sid) => bindConnector(c, sid)}
                    onCreate={() => setCreateFor(c.key)}
                  />
                ))}
              </div>
            ) : null}

            {mcpEntries.length ? (
              <div className="space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">MCP servers</div>
                {mcpEntries.map((m) => {
                  const reg = mcpRegistry.find((r) => r.id === m.name || r.displayName === m.name);
                  return (
                    <div key={m.key} className="rounded-md border border-gray-200 p-2" data-testid={`toolcfg-mcp-${m.key}`}>
                      <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-gray-700">
                        MCP: {reg?.displayName || m.name}
                        <ScopePill scope={m.scope} agentId={m.agentId} roomContext={m.roomContext} />
                        {done[m.key] ? <span className="text-[10px] font-medium text-emerald-600">✓ configured</span> : null}
                        {savingKey === m.key ? <span className="text-[10px] text-gray-400">saving…</span> : null}
                      </div>
                      {reg ? (
                        <McpCredentialForm
                          mcp={reg} canManage
                          onSave={(_id, credentials) => saveMcp(m, credentials)}
                        />
                      ) : (
                        <p className="text-[11px] text-gray-400">
                          {m.name} isn’t in this workspace’s MCP registry — configure it in the room after deploy.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </>
        )}
        {error ? <p className="text-sm text-red-600" data-testid="toolcfg-error">{error}</p> : null}
      </div>

      <div className="mt-3 flex shrink-0 items-center justify-between border-t border-gray-100 pt-3">
        <span className="text-[11px] text-gray-400">
          {allDone ? 'All set — nothing left to configure.' : 'You can save partially and finish later.'}
        </span>
        <button type="button" onClick={() => onDone?.()} data-testid="toolcfg-done"
          className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700">
          {allDone ? doneLabel : `Save ${configuredCount}/${total} & continue`}
        </button>
      </div>

      {/* Inline "create connection" — the SAME registry wizard the deploy wizard uses;
          creates a reusable TENANT secret, then binds it at this panel's scope. */}
      {createForEntry ? (
        createForEntry.name === 'generic-rest-api' ? (
          <ToolGenericRestConfigWizard
            key={`create-${createForEntry.key}`}
            tool={{ name: 'generic-rest-api' }} open isNewEntry
            persistTarget="registry" registryScope={{ kind: 'TENANT' }}
            networkId={REGISTRY_PROBE_NETWORK_ID} agentId="registry-ui" tenantId={tenantId}
            onRegistryCreated={handleConnectorCreated}
            onCancel={() => setCreateFor(null)}
          />
        ) : (
          <ToolCodeConnectorRegistryWizard
            key={`create-${createForEntry.key}`}
            tool={{ name: createForEntry.name }} open isNewEntry
            registryScope={{ kind: 'TENANT' }} tenantId={tenantId}
            networkId={REGISTRY_PROBE_NETWORK_ID}
            onRegistryCreated={handleConnectorCreated}
            onCancel={() => setCreateFor(null)}
          />
        )
      ) : null}
    </div>
  );
}
